'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Pair = 'VI' | 'VR' | 'IR' | 'VP' | 'IP' | 'RP';

const PAIR_LABELS: Record<Pair, { a: string; b: string; description: string }> = {
  VI: { a: 'Voltage (V)', b: 'Current (A)', description: 'Voltage and current' },
  VR: { a: 'Voltage (V)', b: 'Resistance (Ω)', description: 'Voltage and resistance' },
  IR: { a: 'Current (A)', b: 'Resistance (Ω)', description: 'Current and resistance' },
  VP: { a: 'Voltage (V)', b: 'Power (W)', description: 'Voltage and power' },
  IP: { a: 'Current (A)', b: 'Power (W)', description: 'Current and power' },
  RP: { a: 'Resistance (Ω)', b: 'Power (W)', description: 'Resistance and power' },
};

function solve(pair: Pair, a: number, b: number) {
  switch (pair) {
    case 'VI':
      return { V: a, I: b, R: b !== 0 ? a / b : 0, P: a * b };
    case 'VR':
      return { V: a, I: b !== 0 ? a / b : 0, R: b, P: b !== 0 ? (a * a) / b : 0 };
    case 'IR':
      return { V: a * b, I: a, R: b, P: a * a * b };
    case 'VP':
      return { V: a, I: a !== 0 ? b / a : 0, R: b !== 0 ? (a * a) / b : 0, P: b };
    case 'IP':
      return { V: a !== 0 ? b / a : 0, I: a, R: a !== 0 ? b / (a * a) : 0, P: b };
    case 'RP':
      return {
        V: Math.sqrt(Math.max(0, b * a)),
        I: a !== 0 ? Math.sqrt(Math.max(0, b / a)) : 0,
        R: a,
        P: b,
      };
  }
}

export default function BrowserOhmsLawCalculator() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('ohms-law-calculator', '').catch(() => undefined);
  };

  const [pair, setPair] = useState<Pair>('VI');
  const [valueA, setValueA] = useState(12);
  const [valueB, setValueB] = useState(2);

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => solve(pair, valueA, valueB), [pair, valueA, valueB]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Electronics</span>
        <h2 className="tool-live-title">Ohm&apos;s law calculator</h2>
        <p className="tool-live-subtitle">
          Enter any 2 of voltage, current, resistance, or power to get the other 2.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field tool-live-field-full">
            <span>Which 2 values do you know?</span>
            <select onChange={(e) => onChange(setPair)(e.target.value as Pair)} value={pair}>
              {(Object.keys(PAIR_LABELS) as Pair[]).map((p) => (
                <option key={p} value={p}>
                  {PAIR_LABELS[p].description}
                </option>
              ))}
            </select>
          </label>
          <label className="tool-live-field">
            <span>{PAIR_LABELS[pair].a}</span>
            <input
              onChange={(e) => onChange(setValueA)(Number(e.target.value) || 0)}
              step="any"
              type="number"
              value={valueA}
            />
          </label>
          <label className="tool-live-field">
            <span>{PAIR_LABELS[pair].b}</span>
            <input
              onChange={(e) => onChange(setValueB)(Number(e.target.value) || 0)}
              step="any"
              type="number"
              value={valueB}
            />
          </label>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>{result.V.toFixed(3)} V</strong>
            <span>Voltage</span>
          </p>
          <p className="tool-live-panel-note">
            <strong>{result.I.toFixed(3)} A</strong>
            <span>Current</span>
          </p>
          <p className="tool-live-panel-note">
            <strong>{result.R.toFixed(3)} Ω</strong>
            <span>Resistance</span>
          </p>
          <p className="tool-live-panel-note">
            <strong>{result.P.toFixed(3)} W</strong>
            <span>Power</span>
          </p>
        </section>
      </div>
    </div>
  );
}
